import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import validator from 'validator';
import BuyButton from '@tovia/man-ui/lib/components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/pro-light-svg-icons';
import { validatorOptions } from 'src/shared/constants/signup';
import { EmailAvailabilityError, useEmailAvailability } from 'src/client/requests/useEmailAvailability';
import { useLocation } from 'react-router-dom';

type FormData = {
  email: string;
  password: string;
};

type Props = {
  className?: string;
  onSubmit: (values: FormData) => void | Promise<void>;
  submitted?: boolean;
};

type FormTouched = Partial<Record<keyof FormData, boolean>>;

const INITIAL_VALUES: FormData = { email: '', password: '' };

const getEmailFormatError = (email: string): string | undefined => {
  if (!email) {
    return undefined;
  }

  if (!validator.isEmail(email, validatorOptions)) {
    return 'Invalid email address';
  }

  return undefined;
};

const getPasswordError = (password: string): string | undefined => {
  if (!password) {
    return undefined;
  }

  if (password.length < 4) {
    return 'Invalid password';
  }

  return undefined;
};

export const PurchaseGalleryForm = (props: Props) => {
  const { className = '', onSubmit, submitted = false } = props;

  const location = useLocation();
  const [values, setValues] = useState<FormData>(INITIAL_VALUES);
  const [touched, setTouched] = useState<FormTouched>({});
  const [validatedEmail, setValidatedEmail] = useState<string | null>(null);
  const [emailAsyncError, setEmailAsyncError] = useState<React.ReactNode | null>(null);

  const { validateEmail, isValidating: isEmailValidating, reset: resetEmailValidation } = useEmailAvailability();

  const markFieldTouched = useCallback((field: keyof FormData) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  const emailSyncError = useMemo(() => getEmailFormatError(values.email), [values.email]);
  const passwordError = useMemo(() => getPasswordError(values.password), [values.password]);
  const emailError = emailSyncError ?? emailAsyncError;

  const buildLoginHref = useCallback(
    (email: string): string => {
      const sanitizedEmail = email.trim();
      const loginUrl = new URL('/login', window.location.origin);

      const path = location.pathname.replace(/^\//, '');
      const returnPath = path ? `${path}${location.search}` : location.search || '';
      loginUrl.searchParams.set('r', returnPath);
      loginUrl.searchParams.set('email', sanitizedEmail);

      return loginUrl.toString();
    },
    [location.pathname, location.search],
  );

  const setAccountExistsError = useCallback(
    (email: string) => {
      setEmailAsyncError(
        <>
          Please <LoginLink href={buildLoginHref(email)}>log in</LoginLink> to continue with this email.
        </>,
      );
    },
    [buildLoginHref],
  );

  const clearEmailValidationState = useCallback(() => {
    setValidatedEmail(null);
    setEmailAsyncError(null);
    resetEmailValidation();
  }, [resetEmailValidation]);

  const handleInputChange = useCallback(
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setValues((prev) => {
        const nextValues = { ...prev, [field]: value };
        if (field === 'email') {
          clearEmailValidationState();
        }
        return nextValues;
      });
    },
    [clearEmailValidationState],
  );

  const validateEmailIfNeeded = useCallback(
    async (email: string): Promise<boolean> => {
      if (!email) {
        setEmailAsyncError(null);
        return true;
      }

      if (validatedEmail === email) {
        return true;
      }

      try {
        await validateEmail(email);
        setEmailAsyncError(null);
        setValidatedEmail(email);
        return true;
      } catch (error: unknown) {
        if (error instanceof EmailAvailabilityError && error.reason === 'account_exists') {
          const emailForLink = error.email ?? email;
          setAccountExistsError(emailForLink);
        } else if (error instanceof Error) {
          setEmailAsyncError(error.message);
        } else {
          setEmailAsyncError('Unable to verify email address');
        }

        setValidatedEmail(null);
        return false;
      }
    },
    [setAccountExistsError, validateEmail, validatedEmail],
  );

  const handleBlur = useCallback(
    (field: keyof FormData) => async () => {
      markFieldTouched(field);

      if (field === 'email') {
        if (!getEmailFormatError(values.email)) {
          await validateEmailIfNeeded(values.email);
        }
      }
    },
    [markFieldTouched, validateEmailIfNeeded, values.email],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setTouched({ email: true, password: true });

      const emailFormatError = getEmailFormatError(values.email);
      const passwordValidationError = getPasswordError(values.password);

      if (emailFormatError || passwordValidationError || !values.email || !values.password) {
        return;
      }

      const emailIsValid = await validateEmailIfNeeded(values.email);
      if (!emailIsValid) {
        return;
      }

      void onSubmit(values);
    },
    [onSubmit, validateEmailIfNeeded, values],
  );

  const emailErrorVisible = (touched.email || submitted) && Boolean(emailError);
  const passwordErrorVisible = (touched.password || submitted) && Boolean(passwordError);
  const isFormIncomplete = !values.email || !values.password;
  const hasErrors = Boolean(emailError) || Boolean(passwordError) || isFormIncomplete;

  return (
    <FormContainer onSubmit={handleSubmit} className={className} noValidate>
      <FieldWrapper>
        {isEmailValidating && <SpinnerIcon icon={faCircleNotch} spin />}
        <InputField
          $isValidating={isEmailValidating}
          id="email-gallery-purchase"
          name="email"
          placeholder="Enter your email"
          type="email"
          value={values.email}
          onChange={handleInputChange('email')}
          onBlur={handleBlur('email')}
        />
        {emailErrorVisible && <ErrorText>{emailError}</ErrorText>}
      </FieldWrapper>
      <FieldWrapper>
        <InputField
          id="password-gallery-purchase"
          name="password"
          placeholder="Enter your password"
          type="password"
          value={values.password}
          onChange={handleInputChange('password')}
          onBlur={handleBlur('password')}
        />
        {passwordErrorVisible && <ErrorText>{passwordError}</ErrorText>}
      </FieldWrapper>
      <SubmitButton
        className="btn btn-primary"
        loading={submitted && !hasErrors}
        type="submit"
        disabled={submitted || isEmailValidating || hasErrors}
        spinnerClass="buy-spinner"
      >
        Continue to watch
      </SubmitButton>
    </FormContainer>
  );
};

const FormContainer = styled.form`
  position: relative;
`;

const FieldWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const InputField = styled.input<{ $isValidating?: boolean }>`
  display: block;
  width: 100%;
  height: 44px;
  padding: 0 10px;
  border: 1px solid #888;
  border-radius: 3px;
  padding-right: ${(props) => (props.$isValidating ? '34px' : '10px')};
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  color: red;
`;

const LoginLink = styled.a`
  color: #1390ff !important;

  &:hover {
    text-decoration: underline;
  }
`;

const SpinnerIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 10px;
  right: 22px;
  width: 24px;
  height: 24px;
  opacity: 0.5;
`;

const SubmitButton = styled(BuyButton)`
  position: relative;
  border: 0 none;
  display: inline-block;
  flex-grow: 1;
  font-size: 1.3rem;
  height: 48px;
  margin: 16px 0;
  text-align: center;
  text-transform: uppercase;
  width: 100%;

  &.loading {
    background-color: #9d9d9d;
    color: transparent !important;
  }

  .buy-spinner {
    display: block;
    height: 0;
    position: relative;
    width: 100%;

    .spinner {
      width: 28px;
      height: 28px;

      path {
        fill: #888888;
      }
    }
  }
`;

export default PurchaseGalleryForm;
